import React, { Context, ReactNode, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import scrollIntoView from 'scroll-into-view-if-needed';
import { animate, easeInOut } from 'popmotion';
import Styler from 'stylefire';

const ONE_SECOND = 1000;

interface AnchorLinksProviderProps {
    AnchorLinksContext: Context<AnchorLinksProviderValue>;
    /**
     * Time to wait for the DOM to populate before ignoring further mutations
     */
    delay: number;
    children: ReactNode;
    /**
     * An element ID that refers to the main content area
     */
    mainContentId: string;
    /**
     * A list of {@link AnchorLocationRule} objects. If no matching rule is found for a given
     * location, the general `delay` is used.
     */
    rules?: AnchorLocationRule[];
}

/**
 * A provider that allows anchor links in React to work like they do in static HTML sites. When the
 * page loads or the location changes and the URL contains a hash, i.e. a part that begins with '#',
 * this hook will use a MutationObserver check for a DOM element with an ID that matches that hash,
 * including the '#' character. If such an ID is found within the given `delay`, that element will
 * scroll into view. This delay logic is necessary because SPA frameworks like React mutate the DOM
 * after the URI history state changes.
 * Based off of: {@link https://gist.github.com/gajus/0bbc78135d88a02c18366f12237011a5}
 *
 * @remarks
 * Things get tricky when calling useAnchorLinks() on a page that contains a modal, such as the
 * Reports page. This is because we want to pause scrolling to anchor links when the modal is open
 * and just after it closes, so that the user doesn't see auto-scrolling when they shouldn't. To
 * help with this, you can set a flag for `noAnchorScroll` in the react-router-dom location.state.
 * See an example of this in: packages/web/src/components/pages/Reports/Reports.tsx
 *
 * See packages/web/src/components/pages/Statements/components/StatementsCharts/StatementsCharts.tsx
 * and the <AnchorLinksProvider> instance in packages/web/src/app/App.tsx for an example of how
 * `setActiveScrollObservers` works with `rules`.
 * @param props - {@link AnchorLinksProviderProps}
 */
export const AnchorLinksProvider = ({
    AnchorLinksContext,
    delay,
    children,
    mainContentId,
    rules,
}: AnchorLinksProviderProps): JSX.Element => {
    const history = useHistory<{ noAnchorScroll?: boolean }>();
    const observerRef = useRef<MutationObserver | null>(null);
    const timeoutIdRef = useRef<number | null>(null);
    const [activeScrollObservers, setActiveScrollObservers] = useState<
        Record<string, boolean>
    >({});

    /**
     * Clear the timeoutId and stop listening for DOM mutations
     */
    const reset = (): void => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);

            timeoutIdRef.current = null;
        }

        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        return undefined;
    };

    /**
     * Create a function that scrolls an element into view based on its ID
     * @param id - the desired element ID
     * @returns a function that returns true if the element was scrolled into view
     */
    const createScrollToElement = (id: string) => (): boolean => {
        const element = document.getElementById(id);

        if (element) {
            const prefersReducedMotion = window.matchMedia(
                '(prefers-reduced-motion: reduce)',
            ).matches;

            scrollIntoView(element, {
                behavior: actions => {
                    actions.forEach(action => {
                        const { top, el } = action;

                        if (el.id === mainContentId) {
                            if (prefersReducedMotion) {
                                // If the user prefers reduced motion, scroll without animation
                                el.scrollTop = top;
                            } else {
                                // Otherwise, scroll with animation
                                const styler = Styler(el);

                                animate({
                                    from: el.scrollTop,
                                    to: top,
                                    ease: easeInOut,
                                    onUpdate: nextTop => {
                                        styler.set('scrollTop', nextTop);
                                    },
                                });
                            }
                        }
                    });
                },
            });

            reset();

            return true;
        }

        return false;
    };

    /**
     * Set up the timeout and MutationObserver that will scroll to an element when it exists
     * @param location a history.location object
     * @returns undefined
     */
    const setUpScroll = (
        location: import('history').Location<{ noAnchorScroll?: boolean }>,
    ): void => {
        const rule =
            rules === undefined
                ? undefined
                : rules.find(
                      ({ pathname }: AnchorLocationRule): boolean =>
                          pathname === location.pathname,
                  );

        if (
            window?.MutationObserver === undefined ||
            location.state?.noAnchorScroll
        ) {
            // Bail if the browser doesn't support MutationObserver or if the last location change
            // was made with location.state.noAnchorScroll
            return undefined;
        }

        const elementId = location.hash;

        // We include the '#' in the element id, so any hash that we care about must be longer than
        // one character, like `#foo`
        if (typeof elementId !== 'string' || elementId.length < 2) {
            const contentArea = document.getElementById(mainContentId);

            if (contentArea) {
                // Initialize the container's scroll position to be scrolled to the top
                contentArea.scrollTop = 0;
            }

            return undefined;
        }

        const scrollToElement = createScrollToElement(elementId);

        // If we can scroll to the element immediately, do so and then return
        if (scrollToElement()) {
            return undefined;
        }

        // Otherwise, create a MutationObserver that will scroll to the element when it exists
        observerRef.current = new MutationObserver(scrollToElement);

        observerRef.current.observe(document, {
            attributes: true,
            childList: true,
            subtree: true,
        });

        if (rule?.type === 'delay' && rule?.delay !== undefined) {
            // If there is a matching rule that has type 'delay', use rule.delay to call setTimeout
            timeoutIdRef.current = window.setTimeout(reset, rule.delay);
        } else if (rule?.type === 'status' && rule?.pathname !== undefined) {
            // If there is a matching rule that has type 'status', set a flag in activeScrollObservers to
            // indicate that there is an active observer for this pathname
            setActiveScrollObservers(
                (prev: Record<string, boolean>): Record<string, boolean> => ({
                    ...prev,
                    [location.pathname]: true,
                }),
            );
        } else {
            // If there is no rule for this pathname, use the global delay to call setTimeout
            timeoutIdRef.current = window.setTimeout(reset, delay);
        }

        return undefined;
    };

    const {
        location: { pathname },
    } = history;

    useEffect(() => {
        const activePathname = activeScrollObservers?.[pathname] ?? null;

        let timeoutId: Maybe<number> = null;

        if (activePathname !== null) {
            // If there is an observer for this pathname that should no longer be active:
            if (activePathname === false) {
                // Wait one more second and then
                timeoutId = window.setTimeout(() => {
                    // 1) Call reset() to stop the observer
                    reset();

                    // 2) Remove this pathname from activeScrollObservers
                    setActiveScrollObservers(
                        (
                            prev: Record<string, boolean>,
                        ): Record<string, boolean> => {
                            const next = { ...prev };

                            delete next[pathname];

                            return next;
                        },
                    );
                }, ONE_SECOND);
            }
        }

        return () => {
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [pathname]);

    useEffect(() => {
        history.listen((location, action) => {
            reset();

            if (action !== 'PUSH') {
                return;
            }

            // Set up scroll on every PUSH action (i.e. location change)
            setUpScroll(location);
        });
    }, []);

    useEffect(() => {
        requestAnimationFrame(() => {
            // Set up scroll when the provider mounts
            setUpScroll(history.location);
        });

        return reset;
    }, [history.location]);

    return (
        <AnchorLinksContext.Provider
            value={{
                activeScrollObservers,
                setActiveScrollObservers,
            }}
        >
            {children}
        </AnchorLinksContext.Provider>
    );
};
